import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNCountryFlag from 'components/KNCountryFlag/KNCountryFlag'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableColumn } from 'components/KNDataTable/types'
import { useQuotesView } from 'context/QuotesViewContext'
import { compactLocalDate, zonedDate } from 'global/helpers/dates'
import { replaceHighlights } from 'global/helpers/highlights'
import { formatDecimals } from 'global/helpers/numberFormatters'
import { type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import type { QuoteRequest, Stop } from 'screens/QuoteDetails/QuoteDetails.types'
import QuoteHeader from 'screens/Quotes/QuoteHeader'
import QuoteCargo from './QuoteCargo'
import { QUOTES_QUERY_KEY } from './Quotes.service'
import QuoteStatusChip from './QuoteStatusChip'

interface QuoteTableProps {
  readonly quotes: QuoteRequest[]
  readonly pageCount?: number
}

interface QuoteStopProps {
  stop: Stop
  keywords?: string[]
}

const QuoteStop = ({ stop, keywords }: QuoteStopProps): ReactElement => (
  <>
    <Typography variant="p3" noWrap>{zonedDate(stop.windowStartDate, 'full_no_year')}</Typography>
    <Typography component="p" variant="p3" color="primary.main" noWrap>
      <KNCountryFlag countryCode={stop.address.countryCode} />
      {replaceHighlights(`${stop.address.countryCode}, ${stop.address.city}`, keywords)}
    </Typography>
  </>
)

const QuoteTable = ({ quotes, pageCount }: QuoteTableProps): ReactElement => {
  const { t } = useTranslation()
  const { filters, page, setPage, saveScrollPosition } = useQuotesView()
  const isFetching = useIsFetching({ queryKey: [QUOTES_QUERY_KEY] })
  const history = useHistory()

  const handleRowClick = useCallback((row: QuoteRequest): void => {
    saveScrollPosition()
    history.push(`/quotes/${row.token}`)
  }, [history])

  const statusColumnValue = (row: QuoteRequest): ReactElement => (
    <QuoteStatusChip status={row.status} />
  )

  const transportDetailsColumnValue = (row: QuoteRequest): ReactElement => {
    return (
      <Stack spacing={0.25}>
        <QuoteHeader quoteRequest={row} />
        <QuoteCargo quoteRequest={row} />
      </Stack>
    )
  }

  const firstPickupLastDeliveryColumnValue = (row: QuoteRequest): ReactElement => {
    const firstShipment = row.shipments[0]
    const lastShipment = row.shipments.at(-1)

    if (!firstShipment || !lastShipment) {
      return <Typography variant="p3" color="primary.light">—</Typography>
    }

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <QuoteStop stop={firstShipment.pickup} keywords={filters.keywords} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <QuoteStop stop={lastShipment.delivery} keywords={filters.keywords} />
        </Grid>
      </Grid>
    )
  }

  const expirationColumnValue = (row: QuoteRequest): ReactElement => (
    <Typography variant="p3" color="text.main" noWrap>{compactLocalDate(row.expirationDate)}</Typography>
  )

  const quoteColumnValue = (row: QuoteRequest): ReactElement =>
    row.amount ? (
      <Typography variant="p3" color="text.main" noWrap>
        {formatDecimals(row.amount, { currency: row.currency })}
      </Typography>
    ) : (
      <Typography variant="p3" color="primary.light">—</Typography>
    )

  const columns = useMemo((): KNDataTableColumn<QuoteRequest>[] => [
    {
      name: 'status',
      label: t('screens.cs.quotes.card.columns.status'),
      getValue: statusColumnValue,
      sx: { width: '8rem' },
    },
    {
      name: 'transport_details',
      label: t('screens.cs.quotes.card.columns.transport_details'),
      getValue: transportDetailsColumnValue,
      sx: { width: { xs: '20rem', xxl: 'auto' } },
    },
    {
      name: 'first_pickup_last_delivery',
      label: t('screens.cs.quotes.card.columns.first_pickup_last_delivery'),
      getValue: firstPickupLastDeliveryColumnValue,
      sx: { width: { xs: '20rem', xxl: '28rem' } },
    },
    {
      name: 'expiration',
      label: t('screens.cs.quotes.card.columns.expiration'),
      getValue: expirationColumnValue,
      sx: { width: '14rem' },
    },
    {
      name: 'quote',
      label: t('screens.cs.quotes.card.columns.quote'),
      getValue: quoteColumnValue,
      sx: { width: '14rem' },
    },
  ], [filters.keywords])

  return (
    <KNDataTable
      columns={columns}
      data={quotes}
      page={page}
      pageCount={pageCount}
      onPageChange={setPage}
      onRowClick={handleRowClick}
      isLoading={isFetching > 0}
      sx={{
        marginX: -2,
      }}
      tableSx={{
        minWidth: '68rem',
      }}
    />
  )
}

export default QuoteTable
